import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../context/UserContext';
import { Card, Checkbox, SegmentedControl, useToast } from '../../components/ui';

type Level = 'ding' | 'quiet' | 'off';
type LevelField = 'agent_queue_level' | 'mention_level' | 'uptime_level' | 'comms_sync_level';

interface Prefs {
  agent_queue_level: Level;
  agent_queue_scope: 'all' | 'mine';
  agent_queue_statuses: string[];
  mention_level: Level;
  uptime_level: Level;
  comms_sync_level: Level;
}

// Mirrors the column defaults in notification_preferences: no row = these.
const DEFAULTS: Prefs = {
  agent_queue_level: 'off',
  agent_queue_scope: 'mine',
  agent_queue_statuses: ['spec_ready', 'build_ready', 'unwind_ready', 'complete'],
  mention_level: 'ding',
  uptime_level: 'ding',
  comms_sync_level: 'ding',
};

const LEVELS = [
  { id: 'ding', label: 'Ding' },
  { id: 'quiet', label: 'Bell only' },
  { id: 'off', label: 'Off' },
];

const SCOPES = [
  { id: 'mine', label: 'Jobs I submitted' },
  { id: 'all', label: 'All jobs' },
];

const STATUSES = [
  { id: 'spec_ready', label: 'Spec ready' },
  { id: 'build_ready', label: 'Build ready' },
  { id: 'unwind_ready', label: 'Unwind done' },
  { id: 'complete', label: 'Complete' },
];

const CATEGORIES: { field: LevelField; label: string; hint: string }[] = [
  { field: 'agent_queue_level', label: 'Agent queue', hint: 'A job needs a person: spec or build to review, unwind done, or complete.' },
  { field: 'mention_level', label: 'Mentions', hint: 'Someone @mentions you in chat.' },
  { field: 'uptime_level', label: 'Site down / recovered', hint: 'The uptime monitor loses or regains a client site.' },
  { field: 'comms_sync_level', label: 'Trello sync errors', hint: 'Client Comms can’t reach Trello.' },
];

const muted: React.CSSProperties = { margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 };
const rowLabel: React.CSSProperties = {
  fontSize: 'var(--text-sm)',
  fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
  color: 'var(--text-body)',
};

/** Settings → Notifications: which alerts reach this person, and how loudly. */
export default function NotificationPreferencesCard() {
  const { user } = useUser();
  const toast = useToast();
  const [prefs, setPrefs] = useState<Prefs>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('notification_preferences')
      .select('agent_queue_level, agent_queue_scope, agent_queue_statuses, mention_level, uptime_level, comms_sync_level')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.error('Failed to load notification preferences:', error);
          toast.error('Couldn’t load your notification settings');
        } else if (data) {
          setPrefs(data as Prefs);
        }
        setLoading(false);
      });
  }, [user, toast]);

  const save = async (patch: Partial<Prefs>) => {
    if (!user) return;
    const previous = prefs;
    const next = { ...prefs, ...patch };
    setPrefs(next);
    const { error } = await supabase
      .from('notification_preferences')
      .upsert({ user_id: user.id, ...next }, { onConflict: 'user_id' });
    if (error) {
      console.error('Failed to save notification preferences:', error);
      setPrefs(previous);
      toast.error('Couldn’t save that change', 'Your notification settings were not updated.');
    }
  };

  const toggleStatus = (status: string, on: boolean) => {
    const statuses = on
      ? STATUSES.map((s) => s.id).filter((id) => id === status || prefs.agent_queue_statuses.includes(id))
      : prefs.agent_queue_statuses.filter((s) => s !== status);
    save({ agent_queue_statuses: statuses });
  };

  return (
    <Card eyebrow="Notifications" title="What dings you">
      <p style={{ ...muted, marginBottom: 'var(--space-4)' }}>
        <strong style={{ color: 'var(--text-body)' }}>Ding</strong> lands in the alerts bell, chimes in an open Cockpit
        tab and pushes to your devices. <strong style={{ color: 'var(--text-body)' }}>Bell only</strong> lands in the bell
        quietly. <strong style={{ color: 'var(--text-body)' }}>Off</strong> skips it.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : undefined }}>
        {CATEGORIES.map(({ field, label, hint }, i) => (
          <div
            key={field}
            style={{
              padding: 'var(--space-4) 0',
              borderTop: i === 0 ? 'none' : '1px solid var(--border-hairline)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={rowLabel}>{label}</div>
                <p style={muted}>{hint}</p>
              </div>
              <SegmentedControl options={LEVELS} value={prefs[field]} onChange={(id) => save({ [field]: id as Level })} />
            </div>

            {field === 'agent_queue_level' && prefs.agent_queue_level !== 'off' ? (
              <div
                style={{
                  marginTop: 'var(--space-3)',
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'var(--surface-raised)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-3)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                  <span style={muted}>Which jobs</span>
                  <SegmentedControl
                    options={SCOPES}
                    value={prefs.agent_queue_scope}
                    onChange={(id) => save({ agent_queue_scope: id as Prefs['agent_queue_scope'] })}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                  <span style={muted}>When a job is</span>
                  <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                    {STATUSES.map((s) => (
                      <Checkbox
                        key={s.id}
                        label={<span style={{ fontSize: 'var(--text-sm)' }}>{s.label}</span>}
                        checked={prefs.agent_queue_statuses.includes(s.id)}
                        onChange={(on) => toggleStatus(s.id, on)}
                      />
                    ))}
                  </div>
                </div>
                {prefs.agent_queue_statuses.length === 0 ? (
                  <p style={{ ...muted, color: 'var(--status-warn)' }}>No statuses ticked, so no agent queue alerts will arrive.</p>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}
